import type {
  Contact,
  Nearby,
  Page,
  Payment,
  Rewards,
  Quote,
  Ride,
  Rider,
  RiderSettings,
  SavedPlace,
  ShareLink,
  TokenPair,
  VehicleType,
  LatLng,
  Place,
  ApiPlace,
} from './types';

/** API origin. Empty = same origin (the dev server and production proxy /v1 and /socket.io to the API). */
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

/**
 * GET /v1/auth/config. Older servers send only `phoneCountry` ("+91") and no `methods`; newer ones add
 * `methods` (["phone", "email"]), `defaultCountry` ("IN") and `countries` ("all" or a list of ISO codes).
 */
export type AuthConfig = {
  phoneLogin?: 'sms' | 'dev' | 'off';
  phoneCountry?: string;
  codeLength: number;
  channels?: string[];
  methods?: ('phone' | 'email')[];
  defaultCountry?: string;
  countries?: 'all' | string[];
  /** digits in an email code (SMS codes: codeLength) */
  emailCodeLength?: number;
  emailLogin?: 'email' | 'dev' | 'off';
  /** how riders can pay — ["cash"] for now */
  paymentMethods?: string[];
};
export type SignInTarget = { phone: string } | { email: string };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type TokenStore = {
  get(): { accessToken: string; refreshToken: string } | null;
  set(pair: { accessToken: string; refreshToken: string } | null): void;
};

/** Network failures read the same as server errors, in plain words. */
const offline = () => new ApiError(0, 'NETWORK', 'No connection. Check your internet and try again.');

/**
 * The API client: JSON in/out, Bearer token, and on 401 one refresh (single flight — parallel calls share it;
 * refresh tokens rotate, so two refreshes at once would sign the rider out) then one retry.
 */
export function createClient(store: TokenStore, fetchImpl: typeof fetch = (...a) => fetch(...a), onSignedOut?: () => void) {
  let refreshing: Promise<boolean> | null = null;

  async function raw(method: string, path: string, body?: unknown, token?: string): Promise<Response> {
    try {
      return await fetchImpl(`${API_BASE}${path}`, {
        method,
        headers: {
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw offline();
    }
  }

  async function parse<T>(res: Response): Promise<T> {
    const text = await res.text().catch(() => '');
    let data: { code?: string; message?: string; requestId?: string } | null = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      // not JSON (a proxy's error page, a cut-off reply): judged by the status alone
      if (res.ok) throw new ApiError(res.status, 'BAD_RESPONSE', 'Something went wrong. Please try again.');
    }
    if (!res.ok) {
      throw new ApiError(
        res.status,
        data?.code ?? 'ERROR',
        data?.message ?? 'Something went wrong. Please try again.',
        data?.requestId,
      );
    }
    return data as unknown as T;
  }

  function refresh(): Promise<boolean> {
    if (!refreshing) {
      refreshing = (async () => {
        const cur = store.get();
        if (!cur) return false;
        const res = await raw('POST', '/v1/auth/refresh', { refreshToken: cur.refreshToken });
        if (res.status >= 500 || res.status === 429) return false; // our side or busy: keep the session, try later
        if (!res.ok) {
          store.set(null);
          onSignedOut?.();
          return false;
        }
        const pair = (await res.json()) as TokenPair;
        store.set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
        return true;
      })().finally(() => {
        refreshing = null;
      });
    }
    return refreshing;
  }

  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const first = await raw(method, path, body, store.get()?.accessToken);
    if (first.status === 401 && store.get()) {
      if (await refresh()) return parse<T>(await raw(method, path, body, store.get()?.accessToken));
    }
    return parse<T>(first);
  }

  return {
    call,
    refresh,
    authConfig: () => call<AuthConfig>('GET', '/v1/auth/config'),
    /** A sign-in code to a phone (E.164, "+919876543210") or an email address. */
    requestOtp: (to: SignInTarget) =>
      call<{ sent: boolean; phone?: string; email?: string; expiresInSeconds: number; codeLength: number; devCode?: string }>('POST', '/v1/rider/auth/otp', to),
    verifyOtp: async (to: SignInTarget, code: string) => {
      const res = await call<TokenPair & { isNew: boolean; rider: Rider }>('POST', '/v1/rider/auth/verify', { ...to, code });
      store.set({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      return res;
    },
    /** Adding (or changing) the phone or email on the account: a code to it, then the code back. */
    identifierOtp: (to: SignInTarget) =>
      call<{ sent: boolean; phone?: string; email?: string; codeLength: number; devCode?: string }>('POST', '/v1/rider/me/identifiers/otp', to),
    identifierVerify: (to: SignInTarget, code: string) => call<Rider>('POST', '/v1/rider/me/identifiers/verify', { ...to, code }),
    logout: async () => {
      const cur = store.get();
      store.set(null);
      if (cur) await raw('POST', '/v1/auth/logout', { refreshToken: cur.refreshToken }).catch(() => undefined);
    },
    me: () => call<Rider>('GET', '/v1/rider/me'),
    updateMe: (patch: { name?: string; email?: string; shareTripsAfterDark?: boolean; places?: SavedPlace[] }) =>
      call<Rider>('PUT', '/v1/rider/me', patch),
    settings: () => call<RiderSettings>('GET', '/v1/rider/settings'),
    nearby: (at: LatLng) => call<Nearby>('GET', `/v1/rider/nearby?lat=${at.lat}&lng=${at.lng}`),
    contacts: () => call<Contact[]>('GET', '/v1/rider/contacts'),
    addContact: (name: string, phone: string) => call<Contact>('POST', '/v1/rider/contacts', { name, phone }),
    removeContact: (id: string) => call<{ ok: true }>('DELETE', `/v1/rider/contacts/${id}`),
    quote: (pickup: Place, drop: Place) =>
      call<Quote>('POST', '/v1/rider/quotes', {
        pickup: { lat: pickup.lat, lng: pickup.lng, ...(pickup.address ? { address: pickup.address.slice(0, 300) } : {}) },
        drop: { lat: drop.lat, lng: drop.lng, ...(drop.address ? { address: drop.address.slice(0, 300) } : {}) },
      }),
    /** Cash only for now: online payment needs Razorpay keys (the server answers 503 without them). */
    book: (b: { quoteId: string; vehicleType: VehicleType; pickupAddress?: string; dropAddress?: string; pickupNote?: string; rewardId?: string }) =>
      call<Ride>('POST', '/v1/rider/rides', { ...b, paymentMethod: 'CASH' }),
    rewards: () => call<Rewards>('GET', '/v1/rider/rewards'),
    payment: (id: string) => call<{ payment: Payment | null; online: boolean }>('GET', `/v1/rider/rides/${id}/payment`),
    currentRide: () => call<{ ride: Ride | null }>('GET', '/v1/rider/rides/current'),
    ride: (id: string) => call<Ride>('GET', `/v1/rider/rides/${id}`),
    rides: (page = 1, pageSize = 50) => call<Page<Ride>>('GET', `/v1/rider/rides?page=${page}&pageSize=${pageSize}`),
    cancel: (id: string, reason?: string) => call<Ride>('POST', `/v1/rider/rides/${id}/cancel`, reason ? { reason } : {}),
    rate: (id: string, stars: number, tags: string[], comment?: string) =>
      call<unknown>('POST', `/v1/rider/rides/${id}/rating`, { stars, tags, ...(comment ? { comment } : {}) }),
    tip: (id: string, amountPaise: number) => call<Ride>('POST', `/v1/rider/rides/${id}/tip`, { amountPaise }),
    share: (id: string) => call<ShareLink>('POST', `/v1/rider/rides/${id}/share`),
    unshare: (id: string, linkId: string) => call<{ ok: true }>('DELETE', `/v1/rider/rides/${id}/share/${linkId}`),
    /** Ola place search behind the API (`503 PLACES_UNAVAILABLE` when the server has no key or Ola is down). */
    placesAutocomplete: (q: string, near?: LatLng | null) =>
      call<{ items: ApiPlace[] }>('GET', `/v1/places/autocomplete?q=${encodeURIComponent(q)}${near ? `&lat=${near.lat}&lng=${near.lng}` : ''}`),
    placeById: (id: string) => call<ApiPlace>('GET', `/v1/places/${encodeURIComponent(id)}`),
    reversePlace: (at: LatLng) => call<ApiPlace>('GET', `/v1/places/reverse?lat=${at.lat}&lng=${at.lng}`),
    sos: (b: { rideId?: string; lat?: number; lng?: number; note?: string }) =>
      call<{ id: string; status: string; message: string }>('POST', '/v1/rider/sos', b),
  };
}

export type Api = ReturnType<typeof createClient>;

/** Tokens live in localStorage so a reload keeps the rider signed in. */
export const localTokenStore: TokenStore = {
  get() {
    try {
      const v = localStorage.getItem('zr-session');
      return v ? JSON.parse(v) : null;
    } catch {
      return null;
    }
  },
  set(pair) {
    try {
      if (pair) localStorage.setItem('zr-session', JSON.stringify(pair));
      else localStorage.removeItem('zr-session');
    } catch {
      /* storage blocked: session lasts this tab */
    }
  },
};
